import React from "react";
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import {
  NavigateNext as NavigateNextIcon,
  Folder as FolderIcon,
  Home as HomeIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";
import { themeColors } from "../../utils/theme";
import { useS3Browser } from "../../contexts/S3BrowserContext";

/**
 * S3BreadcrumbNav Component
 *
 * Provides breadcrumb navigation for the current S3 path with support for:
 * - Hierarchical path display with proper S3 path parsing
 * - Click navigation to parent folders and root
 * - Visual hierarchy with icons and styling
 * - Parent directory navigation button
 * - Current location highlighting
 * - Path depth indicator
 * - Uses S3Browser context for state management
 *
 * @returns {JSX.Element} Rendered breadcrumb navigation
 */
const S3BreadcrumbNav: React.FC = () => {
  // Get S3 browser state and navigation actions from context
  const { state, navigateTo, navigateToRoot, navigateUp } = useS3Browser();

  // Get current path from context state
  const currentPath = state.navigation.currentPath;
  const breadcrumbs = state.navigation.breadcrumbs;
  console.log("Current Path:", currentPath);
  // Parse the current path into segments for compatibility
  const pathSegments = currentPath
    ? currentPath.split("/").filter(Boolean)
    : [];

  /**
   * Handle breadcrumb click navigation
   * @param path - Target path to navigate to
   */
  const handleBreadcrumbClick = (path: string) => {
    navigateTo(path);
  };

  /**
   * Handle home/root navigation
   */
  const handleHomeClick = () => {
    navigateToRoot();
  };

  /**
   * Handle parent directory navigation
   */
  const handleParentClick = () => {
    if (pathSegments.length === 0) return;
    navigateUp();
  };

  /**
   * Get display name for a path segment
   */
  const getDisplayName = (segment: string): string => {
    // Decode URI components and clean up segment names
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        separator={
          <NavigateNextIcon
            fontSize="small"
            sx={{ color: themeColors.textSecondary }}
          />
        }
        aria-label="S3 path breadcrumb"
        sx={{
          "& .MuiBreadcrumbs-ol": {
            alignItems: "center",
          },
        }}
      >
        {/* Root/Home breadcrumb */}
        <Link
          component="button"
          variant="body1"
          onClick={handleHomeClick}
          sx={{
            display: "flex",
            alignItems: "center",
            color:
              pathSegments.length === 0
                ? themeColors.primary
                : themeColors.textSecondary,
            textDecoration: "none",
            "&:hover": {
              color: themeColors.primary,
              textDecoration: "underline",
            },
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "inherit",
            fontFamily: "inherit",
          }}
        >
          <HomeIcon fontSize="small" sx={{ mr: 0.5 }} />
          Reports
        </Link>

        {/* Path segment breadcrumbs - use breadcrumbs from context */}
        {breadcrumbs.slice(1).map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 2; // -2 because we sliced off root

          if (isLast) {
            return (
              <Typography
                key={breadcrumb.path}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: themeColors.primary,
                  fontWeight: 500,
                }}
              >
                <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
                {breadcrumb.name}
              </Typography>
            );
          }

          return (
            <Link
              key={breadcrumb.path}
              component="button"
              variant="body1"
              onClick={() => handleBreadcrumbClick(breadcrumb.path)}
              sx={{
                display: "flex",
                alignItems: "center",
                color: themeColors.textSecondary,
                textDecoration: "none",
                "&:hover": {
                  color: themeColors.primary,
                  textDecoration: "underline",
                },
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "inherit",
                fontFamily: "inherit",
              }}
            >
              <FolderIcon fontSize="small" sx={{ mr: 0.5 }} />
              {breadcrumb.name}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Current path info */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Chip
          label={
            pathSegments.length === 0
              ? "Root Directory"
              : `${pathSegments.length} levels deep`
          }
          size="small"
          sx={{
            bgcolor: themeColors.accent,
            color: themeColors.textSecondary,
            fontSize: "0.75rem",
          }}
        />
      </Box>
    </Box>
  );
};

export default S3BreadcrumbNav;
